import { Button, Input, Link, Spinner, TabPanel, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useAccount, useContractRead, useContractWrite, useNetwork, usePrepareContractWrite } from "wagmi";

import BalanceAction from "./BalanceAction";
import useDebounce from "../../helpers/useDebounce";
import { VaultDetails } from "../../types/vault";
import { parseUnits } from "viem";
import { waitForTransaction } from "@wagmi/core";
import { TokenBalanceDetails } from "../../types/tokens";

export default function RedeemTabPanel({
  vaultInfo,
  sourceTokenDetails,
  targetTokenDetails,
}: {
  vaultInfo: VaultDetails | undefined;
  sourceTokenDetails: TokenBalanceDetails | undefined;
  targetTokenDetails: TokenBalanceDetails | undefined;
}) {
  const { chain } = useNetwork();
  const inputRef = useRef<any>(null);

  const { address } = useAccount();
  const [redeemAmount, setRedeemAmount] = useState<bigint | undefined>();
  const debouncedRedeemAmount = useDebounce<bigint | undefined>(redeemAmount, 500);

  const {
    data: previewRedeemData,
    error: previewRedeemErr,
    isLoading: isLoadingPreviewRedeemAmt,
    isSuccess: isSuccessPreviewRedeemAmt,
  } = useContractRead({
    address: vaultInfo?.vaultAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "owner",
            type: "address",
          },
          {
            internalType: "uint256",
            name: "numShares",
            type: "uint256",
          },
        ],
        name: "previewRedeem",
        outputs: [
          {
            internalType: "uint256",
            name: "",
            type: "uint256",
          },
          {
            internalType: "uint256",
            name: "",
            type: "uint256",
          },
        ],
        stateMutability: "pure",
        type: "function",
      },
    ],
    functionName: "previewRedeem",
    // @ts-ignore
    args: [address, debouncedRedeemAmount],
    enabled: address && Boolean(debouncedRedeemAmount),
  });

  // @ts-ignore
  const { config: redeemConfig } = usePrepareContractWrite({
    address: vaultInfo?.vaultAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "uint256",
            name: "numShares",
            type: "uint256",
          },
          {
            internalType: "address",
            name: "receiver",
            type: "address",
          },
          {
            internalType: "address",
            name: "owner",
            type: "address",
          },
        ],
        name: "redeem",
        outputs: [
          {
            internalType: "uint256",
            name: "amtSourceToken",
            type: "uint256",
          },
          {
            internalType: "uint256",
            name: "amtTargetToken",
            type: "uint256",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
    ],
    functionName: "redeem",
    // @ts-ignore
    args: [debouncedRedeemAmount, address, address],
    enabled: address != null && debouncedRedeemAmount != null && debouncedRedeemAmount > 0,
  });
  const {
    data: redeemRx,
    error: redeemErr,
    isError: isRedeemError,
    isLoading: isRedeemLoading,
    isSuccess: isRedeemSuccess,
    writeAsync: redeemWrite,
  } = useContractWrite(redeemConfig);

  return (
    <TabPanel>
      <BalanceAction
        inputRef={inputRef}
        setAmount={setRedeemAmount}
        token={vaultInfo?.vaultAddress}
        tokenName={vaultInfo?.vaultSymbol}
        tokenDecimals={Number(vaultInfo?.vaultDecimals)}
      />

      <Input
        display="block"
        type="number"
        size="lg"
        step={1}
        min={0}
        minW="8em"
        mb="0.5em"
        onChange={(evt) => {
          if (evt.target.value) setRedeemAmount(parseUnits(evt.target.value, Number(vaultInfo?.vaultDecimals)));
          else setRedeemAmount(undefined);
        }}
        placeholder={"1000 " + (vaultInfo ? vaultInfo?.vaultSymbol : "shares")}
        disabled={isRedeemLoading}
        variant="flushed"
        ref={inputRef}
      />

      {/* withdraw button */}
      <Button
        display="block"
        size="sm"
        colorScheme="red"
        isDisabled={!address || isRedeemLoading || !redeemAmount || Number(redeemAmount) === 0}
        onClick={() => {
          // should not be able to click without redeemAmount
          redeemWrite?.()
            .then((tx) => {
              waitForTransaction({ hash: tx.hash }).then((rx) => {
                // clear input
                console.log("withdrawn!", rx);
                inputRef.current.value = "";
                setRedeemAmount(undefined);
              });
            })
            .catch((err) => {
              console.log("REDEEM_ERR", err); // TODO: TBD
            });
        }}
      >
        Redeem {vaultInfo?.vaultSymbol}
        {isRedeemLoading && <Spinner size="xs" ml="1em" />}
      </Button>

      {/* preview redeem msgs */}
      {isLoadingPreviewRedeemAmt && <Spinner size="xs" mt="1em" />}
      {isSuccessPreviewRedeemAmt && previewRedeemData && debouncedRedeemAmount !== undefined && (
        <Text display="block" mt="1em" fontSize="xs">
          {/* TODO: format units, etc */}
          You will receive approx. {previewRedeemData[0]?.toString() + " " + sourceTokenDetails?.symbol}&nbsp;+&nbsp;
          {previewRedeemData[1]?.toString() + " " + targetTokenDetails?.symbol}
        </Text>
      )}
      {previewRedeemErr && (
        <Text display="block" mt="1em" fontSize="xs" colorScheme="red">
          Error previewing the redemption!
        </Text>
      )}

      {/* redeem msgs */}
      {isRedeemSuccess && (
        <Link
          display="block"
          mt="1em"
          href={chain?.blockExplorers?.default.url + "/tx/" + redeemRx?.hash}
          isExternal
          fontSize="xs"
          color="teal.500"
        >
          Your redemption has been successful!
        </Link>
      )}
      {isRedeemError && (
        <Text display="block" mt="1em" fontSize="xs">
          Something's gone wrong with your redemption. {redeemErr?.message.substring(0, 280)}
        </Text>
      )}
    </TabPanel>
  );
}
